import React, { useEffect, useMemo, useState } from 'react'
import { ModalWrapper } from 'Components'
import fillingStationsService, {
  FillingStation,
} from 'BoostXpressAdmin/Services/fillingStationsService'
import {
  CreateStationAttendantPayload,
  stationAttendantsBXService,
} from 'BoostXpressAdmin/Services/stationAttendantsBXService'
import {
  StationStaffCountry,
  StationStaffRole,
  StationStaffUser,
  stationStaffService,
} from 'BoostXpressAdmin/Services/stationStaffService'
import { handleError, handleSuccess } from 'Helpers'
import { ToastContainer } from 'react-toastify'

type UserRole = 'Attendant' | 'Manager' | 'Associate'
type StaffMode = 'create' | 'find'
type SubmitStage = 'idle' | 'creating' | 'finding' | 'assigning'
type AddUserFormValues = CreateStationAttendantPayload & { role: UserRole }

type Props = {
  isOpen: boolean
  initialRole?: UserRole
  onClose: () => void
  onSuccess: () => void
}

type RequestError = {
  response?: {
    data?: {
      error?: string
      message?: string
      details?: Record<string, { message?: string }>
      errors?: Array<{ errors?: string[] }>
    }
  }
}

const countries: StationStaffCountry[] = ['Nigeria', 'United States', 'Kenya']

const initialValues = (role: UserRole): AddUserFormValues => ({
  firstName: '',
  lastName: '',
  email: '',
  country: 'Nigeria',
  phone: '',
  fillingStationId: '',
  role,
})

const getRequestErrorMessage = (error: unknown) => {
  const data = (error as RequestError).response?.data
  const fieldError = data?.errors?.flatMap((item) => item.errors ?? []).find(Boolean)
  const detailError = Object.values(data?.details ?? {})
    .map((item) => item.message)
    .find(Boolean)

  return fieldError || detailError || data?.error || data?.message || 'Something went wrong.'
}

const toStaffRole = (role: string): StationStaffRole =>
  role === 'Manager' ? 'STATION_MANAGER' : 'STATION_ASSOCIATE'

const roleLabel = (role: StationStaffRole) =>
  role === 'STATION_MANAGER' ? 'Station Manager' : 'Station Associate'

const loadAllStations = async () => {
  const firstResponse = await fillingStationsService.list({ pageNumber: 1, sortOrder: 'ASC' })
  const firstPage = firstResponse.data?.dataset ?? []
  const pageSize = firstResponse.data?.pageSize ?? firstPage.length
  const total = firstResponse.data?.total ?? firstPage.length
  const totalPages = pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1
  const remainingResponses = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      fillingStationsService.list({ pageNumber: index + 2, sortOrder: 'ASC' }),
    ),
  )

  return [...firstPage, ...remainingResponses.flatMap((response) => response.data?.dataset ?? [])]
}

const CreateAttendantModal = ({ isOpen, initialRole = 'Attendant', onClose, onSuccess }: Props) => {
  const [submitStage, setSubmitStage] = useState<SubmitStage>('idle')
  const [isLoadingStations, setIsLoadingStations] = useState(false)
  const [stations, setStations] = useState<FillingStation[]>([])
  const [staffMode, setStaffMode] = useState<StaffMode>('create')
  const [staffUser, setStaffUser] = useState<StationStaffUser | null>(null)
  const [formError, setFormError] = useState('')
  const [assignmentError, setAssignmentError] = useState('')
  const [assignmentSucceeded, setAssignmentSucceeded] = useState(false)
  const [values, setValues] = useState<AddUserFormValues>(() => initialValues(initialRole))

  const isSubmitting = submitStage !== 'idle'
  const isStaffRole = values.role === 'Manager' || values.role === 'Associate'

  useEffect(() => {
    if (!isOpen || staffUser) return
    setValues((current) => ({ ...current, role: initialRole }))
  }, [initialRole, isOpen, staffUser])

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false

    ;(async () => {
      try {
        setIsLoadingStations(true)
        const loadedStations = await loadAllStations()
        if (!cancelled) setStations(loadedStations)
      } catch {
        if (!cancelled) setStations([])
      } finally {
        if (!cancelled) setIsLoadingStations(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isOpen])

  const stationOptions = useMemo(
    () =>
      stations.map((station) => ({
        value: station.id,
        label: [station.businessName, station.branchName].filter(Boolean).join(': ') || station.id,
      })),
    [stations],
  )

  const resetFlow = (role: UserRole = initialRole) => {
    setValues(initialValues(role))
    setStaffMode('create')
    setStaffUser(null)
    setFormError('')
    setAssignmentError('')
    setAssignmentSucceeded(false)
    setSubmitStage('idle')
  }

  const closeAndReset = () => {
    resetFlow()
    onClose()
  }

  const handleClose = () => {
    if (isSubmitting) return

    // Keep an account that still needs assignment available when the modal is reopened.
    if (staffUser && !assignmentSucceeded) {
      onClose()
      return
    }

    closeAndReset()
  }

  const updateRole = (role: UserRole) => {
    setValues((current) => ({ ...current, role }))
    setStaffUser(null)
    setFormError('')
    setAssignmentError('')
    setAssignmentSucceeded(false)
    if (role === 'Attendant') setStaffMode('create')
  }

  const selectStaffMode = (mode: StaffMode) => {
    setStaffMode(mode)
    setStaffUser(null)
    setFormError('')
    setAssignmentError('')
    setAssignmentSucceeded(false)
  }

  const assignStaff = async (user: StationStaffUser) => {
    const stationId = values.fillingStationId.trim()
    if (!stationId) {
      setAssignmentError('Select a filling station before assigning this user.')
      return
    }

    try {
      setSubmitStage('assigning')
      setAssignmentError('')
      await stationStaffService.createAssignment({
        userResourceId: user.resourceId,
        stationId,
        role: toStaffRole(values.role),
      })
      setAssignmentSucceeded(true)
      onSuccess()
      handleSuccess('Staff account assigned successfully.')
    } catch (error) {
      setAssignmentError(getRequestErrorMessage(error))
    } finally {
      setSubmitStage('idle')
    }
  }

  const createStaff = async () => {
    try {
      setSubmitStage('creating')
      setFormError('')
      setAssignmentError('')
      const response = await stationStaffService.createUser({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        country: values.country as StationStaffCountry,
        role: toStaffRole(values.role),
      })
      const createdUser = response.data?.data

      if (!createdUser?.resourceId) {
        throw new Error('The account was created without a usable user ID.')
      }

      setStaffUser(createdUser)
      onSuccess()
      await assignStaff(createdUser)
    } catch (error) {
      setFormError(
        error instanceof Error && !('response' in error)
          ? error.message
          : getRequestErrorMessage(error),
      )
      setSubmitStage('idle')
    }
  }

  const findStaff = async () => {
    try {
      setSubmitStage('finding')
      setFormError('')
      setAssignmentError('')
      const response = await stationStaffService.lookupByPhone(
        values.phone.trim(),
        values.country as StationStaffCountry,
      )
      const foundUser = response.data?.data

      if (!foundUser?.resourceId) {
        throw new Error('No staff account was returned for this phone number.')
      }

      const accountType = foundUser.accountType.trim().toUpperCase()
      if (accountType === 'STATION_MANAGER' || accountType === 'STATION_ASSOCIATE') {
        setValues((current) => ({
          ...current,
          role: accountType === 'STATION_MANAGER' ? 'Manager' : 'Associate',
        }))
      }
      setStaffUser(foundUser)
    } catch (error) {
      setFormError(
        error instanceof Error && !('response' in error)
          ? error.message
          : getRequestErrorMessage(error),
      )
    } finally {
      setSubmitStage('idle')
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (isSubmitting || assignmentSucceeded) return

    if (staffUser) {
      await assignStaff(staffUser)
      return
    }

    if (isStaffRole) {
      if (staffMode === 'find') await findStaff()
      else await createStaff()
      return
    }

    try {
      setSubmitStage('creating')
      setFormError('')
      await stationAttendantsBXService.create({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        country: values.country.trim() || 'Nigeria',
        fillingStationId: values.fillingStationId.trim(),
      })
      onSuccess()
      closeAndReset()
      handleSuccess('Attendant created successfully.')
    } catch (error) {
      setFormError(getRequestErrorMessage(error))
      handleError(error as Parameters<typeof handleError>[0])
    } finally {
      setSubmitStage('idle')
    }
  }

  const canSubmit = staffUser
    ? Boolean(values.fillingStationId.trim())
    : isStaffRole && staffMode === 'find'
      ? Boolean(values.phone.trim() && values.country.trim())
      : Boolean(
          values.firstName.trim() &&
            values.lastName.trim() &&
            values.email.trim() &&
            values.phone.trim() &&
            values.country.trim() &&
            values.fillingStationId.trim() &&
            values.role.trim(),
        )

  const submitLabel = (() => {
    if (submitStage === 'creating') return 'Creating account...'
    if (submitStage === 'finding') return 'Finding staff...'
    if (submitStage === 'assigning') return 'Assigning...'
    if (staffUser) return assignmentError ? 'Retry assignment' : 'Assign to station'
    if (isStaffRole && staffMode === 'find') return 'Find staff'
    if (isStaffRole) return 'Create and assign'
    return 'Create attendant'
  })()

  if (!isOpen) return null

  return (
    <ModalWrapper
      showModal={isOpen}
      closeModal={handleClose}
      title='Add User'
      size='lg'
      hideHeader={false}
    >
      <div className='boostxpress-admin-shell'>
        <form onSubmit={handleSubmit} className='bx-create-attendant'>
          {assignmentSucceeded && staffUser ? (
            <div className='bx-create-attendant__completion' role='status'>
              <div className='bx-create-attendant__completion-icon' aria-hidden='true'>
                &#10003;
              </div>
              <h3>Staff assignment completed</h3>
              <p>
                {staffUser.firstName} {staffUser.lastName} is now assigned as{' '}
                {roleLabel(toStaffRole(values.role))}.
              </p>
              {staffUser.welcomeEmailSent === false ? (
                <div className='bx-create-attendant__notice bx-create-attendant__notice--warning'>
                  The account was created, but the welcome email was not sent. The user may need
                  help receiving their temporary sign-in details.
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <div className='row g-3'>
                <div className='col-12 col-md-6'>
                  <label className='form-label' htmlFor='add-user-role'>
                    Role
                  </label>
                  <select
                    id='add-user-role'
                    className='form-select'
                    value={values.role}
                    onChange={(event) => updateRole(event.target.value as UserRole)}
                    disabled={Boolean(staffUser) || isSubmitting}
                    required
                  >
                    <option value='Attendant'>Attendant</option>
                    <option value='Associate'>Associate</option>
                    <option value='Manager'>Manager</option>
                  </select>
                </div>

                <div className='col-12 col-md-6'>
                  <label className='form-label' htmlFor='add-user-station'>
                    Filling Station
                  </label>
                  <select
                    id='add-user-station'
                    className='form-select'
                    value={values.fillingStationId}
                    onChange={(event) => {
                      setValues((current) => ({
                        ...current,
                        fillingStationId: event.target.value,
                      }))
                      setAssignmentError('')
                    }}
                    required={staffMode === 'create' || Boolean(staffUser) || !isStaffRole}
                    disabled={isLoadingStations || isSubmitting}
                  >
                    <option value=''>
                      {isLoadingStations ? 'Loading stations...' : 'Select a station'}
                    </option>
                    {stationOptions.map((station) => (
                      <option key={station.value} value={station.value}>
                        {station.label}
                      </option>
                    ))}
                  </select>
                  <div className='form-text'>
                    Create missing stations from the Stations page first.
                  </div>
                </div>

                {isStaffRole ? (
                  <div className='col-12'>
                    <div
                      className='bx-create-attendant__mode'
                      role='tablist'
                      aria-label='Staff source'
                    >
                      <button
                        type='button'
                        role='tab'
                        aria-selected={staffMode === 'create'}
                        className={staffMode === 'create' ? 'is-active' : ''}
                        onClick={() => selectStaffMode('create')}
                        disabled={Boolean(staffUser) || isSubmitting}
                      >
                        Create new staff
                      </button>
                      <button
                        type='button'
                        role='tab'
                        aria-selected={staffMode === 'find'}
                        className={staffMode === 'find' ? 'is-active' : ''}
                        onClick={() => selectStaffMode('find')}
                        disabled={Boolean(staffUser) || isSubmitting}
                      >
                        Find existing staff
                      </button>
                    </div>
                  </div>
                ) : null}

                {staffUser ? (
                  <div className='col-12'>
                    <div className='bx-create-attendant__user-card'>
                      <div>
                        <span>
                          {staffMode === 'create' ? 'Account created' : 'Staff account found'}
                        </span>
                        <strong>
                          {staffUser.firstName} {staffUser.lastName}
                        </strong>
                        <small>{staffUser.email}</small>
                        <small>{staffUser.phone}</small>
                      </div>
                      <button
                        type='button'
                        onClick={() => resetFlow(values.role as UserRole)}
                        disabled={isSubmitting}
                      >
                        Use another user
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {staffMode === 'create' || !isStaffRole ? (
                      <>
                        <div className='col-12 col-md-6'>
                          <label className='form-label' htmlFor='add-user-first-name'>
                            First Name
                          </label>
                          <input
                            id='add-user-first-name'
                            type='text'
                            className='form-control'
                            value={values.firstName}
                            onChange={(event) =>
                              setValues((current) => ({
                                ...current,
                                firstName: event.target.value,
                              }))
                            }
                            placeholder='Enter first name'
                            maxLength={50}
                            required
                          />
                        </div>

                        <div className='col-12 col-md-6'>
                          <label className='form-label' htmlFor='add-user-last-name'>
                            Last Name
                          </label>
                          <input
                            id='add-user-last-name'
                            type='text'
                            className='form-control'
                            value={values.lastName}
                            onChange={(event) =>
                              setValues((current) => ({
                                ...current,
                                lastName: event.target.value,
                              }))
                            }
                            placeholder='Enter last name'
                            maxLength={50}
                            required
                          />
                        </div>

                        <div className='col-12 col-md-6'>
                          <label className='form-label' htmlFor='add-user-email'>
                            Email
                          </label>
                          <input
                            id='add-user-email'
                            type='email'
                            className='form-control'
                            value={values.email}
                            onChange={(event) =>
                              setValues((current) => ({ ...current, email: event.target.value }))
                            }
                            placeholder='name@company.com'
                            required
                          />
                        </div>
                      </>
                    ) : null}

                    <div className='col-12 col-md-6'>
                      <label className='form-label' htmlFor='add-user-phone'>
                        Phone
                      </label>
                      <input
                        id='add-user-phone'
                        type='tel'
                        className='form-control'
                        value={values.phone}
                        onChange={(event) =>
                          setValues((current) => ({ ...current, phone: event.target.value }))
                        }
                        placeholder='Enter phone number'
                        maxLength={20}
                        required
                      />
                    </div>

                    <div className='col-12 col-md-6'>
                      <label className='form-label' htmlFor='add-user-country'>
                        Country
                      </label>
                      <select
                        id='add-user-country'
                        className='form-select'
                        value={values.country}
                        onChange={(event) =>
                          setValues((current) => ({ ...current, country: event.target.value }))
                        }
                        required
                      >
                        {countries.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>

              {staffUser?.welcomeEmailSent === false ? (
                <div className='bx-create-attendant__notice bx-create-attendant__notice--warning'>
                  Account created, but the welcome email was not sent. You can still finish the
                  station assignment below.
                </div>
              ) : null}

              {formError ? (
                <div
                  className='bx-create-attendant__notice bx-create-attendant__notice--error'
                  role='alert'
                >
                  {formError}
                </div>
              ) : null}

              {assignmentError ? (
                <div
                  className='bx-create-attendant__notice bx-create-attendant__notice--error'
                  role='alert'
                >
                  <strong>The account is saved, but the station assignment failed.</strong>
                  <span>{assignmentError}</span>
                  <span>Correct the station or role if needed, then retry the assignment.</span>
                </div>
              ) : null}
            </>
          )}

          <div className='bx-create-attendant__actions'>
            {assignmentSucceeded ? (
              <button type='button' className='btn btn-default' onClick={closeAndReset}>
                Done
              </button>
            ) : (
              <>
                <button
                  type='button'
                  className='btn btn-default bx-create-attendant__cancel'
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='btn btn-default'
                  disabled={!canSubmit || isSubmitting}
                >
                  {submitLabel}
                </button>
              </>
            )}
          </div>
        </form>
        <ToastContainer />
      </div>
    </ModalWrapper>
  )
}

export default CreateAttendantModal
